import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import Breadcrums from "../../common/breadcrumbs";
import { formateDateWithMonth, handleServerValidations } from "../../../../utils/commonfunction";
import Loader from "../../common/loader";
import { Details, Status } from "../../services/questionBooking.services";
import { SWAL_SETTINGS } from "../../../../utils/Constants";

const ViewQuestionBooking = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    
    const [booking, setBooking] = useState(null);
    const [loader, setLoader] = useState(true);
    const [statusLoading, setStatusLoading] = useState(false);

    const breadcrumbs = [
        { title: t("link_dashboard"), url: "/admin/dashboard" },
        { title: "Question Bookings", url: "/admin/report/question-bookings/list/1" },
        { title: "View Booking", url: "" },
    ];

    useEffect(() => {
        if (id) {
            setLoader(true);
            Details(id)
                .then((res) => {
                    if (res && res.data) {
                        setBooking(res.data);
                    }
                    setLoader(false);
                })
                .catch((err) => {
                    console.error("Error fetching question booking details:", err);
                    setLoader(false);
                });
        }
    }, [id]);

    const handleStatusUpdate = (newStatus) => {
        Swal.fire({
            title: `Change Status to ${newStatus}?`,
            text: "Are you sure you want to change the status of this booking?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, update it!",
            cancelButtonText: "No, cancel"
        }).then((result) => {
            if (result.isConfirmed) {
                setStatusLoading(true);
                Status(id, newStatus)
                    .then((res) => {
                        if (res.status || res.success) {
                            setBooking(prev => ({ ...prev, status: newStatus }));
                            Swal.fire({
                                icon: "success",
                                text: res.message || "Status updated successfully",
                                ...SWAL_SETTINGS,
                            });
                        } else {
                            Swal.fire({
                                icon: "error",
                                text: handleServerValidations(res),
                                ...SWAL_SETTINGS,
                            });
                        }
                        setStatusLoading(false);
                    })
                    .catch((err) => {
                        console.error(err);
                        setStatusLoading(false);
                    });
            }
        });
    };

    if (loader) {
        return <Loader />;
    }

    if (!booking) {
        return (
            <div className="text-center mt-5">
                <h4>Booking details not found</h4>
                <button className="btn btn-primary mt-3" onClick={() => navigate(-1)}>Go Back</button>
            </div>
        );
    }

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <div className="animation_fade">
                <div className="card custom-card">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h6 className="main-content-label">View Question Booking</h6>
                            <button
                                className="btn btn-secondary"
                                onClick={() => navigate(-1)}
                            >
                                <i className="fa fa-arrow-left me-1"></i> Back
                            </button>
                        </div>

                        <div className="row mb-4">
                            <div className="col-md-6">
                                <h6><strong>User Information</strong></h6>
                                <table className="table table-bordered">
                                    <tbody>
                                        <tr>
                                            <th>Name</th>
                                            <td>{booking?.user?.name || "N/A"}</td>
                                        </tr>
                                        <tr>
                                            <th>Email</th>
                                            <td>{booking?.user?.email || "N/A"}</td>
                                        </tr>
                                        <tr>
                                            <th>Phone</th>
                                            <td>{booking?.user?.phone_number || "N/A"}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div className="col-md-6">
                                <h6><strong>Booking Summary</strong></h6>
                                <table className="table table-bordered">
                                    <tbody>
                                        <tr>
                                            <th>Order Number</th>
                                            <td><strong className="text-primary">{booking?.order_number || `ORD-QB-${booking?.id}`}</strong></td>
                                        </tr>
                                        <tr>
                                            <th>Amount Paid</th>
                                            <td>{booking?.currency_type} {booking?.amount}</td>
                                        </tr>
                                        <tr>
                                            <th>Booking Date</th>
                                            <td>{formateDateWithMonth(booking?.created_at)}</td>
                                        </tr>
                                        <tr>
                                            <th>Status</th>
                                            <td className="d-flex align-items-center justify-content-between">
                                                <span className={`badge ${booking.status === 'answered' ? 'bg-success' : booking.status === 'cancelled' ? 'bg-danger' : 'bg-warning'}`}>
                                                    {booking.status}
                                                </span>
                                                {/* <div className="dropdown">
                                                    <button className="btn btn-sm btn-outline-primary dropdown-toggle" type="button" data-bs-toggle="dropdown" disabled={statusLoading}>
                                                        Update Status
                                                    </button>
                                                    <ul className="dropdown-menu">
                                                        <li><button className="dropdown-item" onClick={() => handleStatusUpdate('pending')}>Pending</button></li>
                                                        <li><button className="dropdown-item" onClick={() => handleStatusUpdate('answered')}>Answered</button></li>
                                                        <li><button className="dropdown-item" onClick={() => handleStatusUpdate('cancelled')}>Cancelled</button></li>
                                                    </ul>
                                                </div> */}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <hr />
                        
                        <div className="row mt-4">
                            <div className="col-md-6">
                                <h6 className="main-content-label mb-3">Submitted User Details</h6>
                                <table className="table table-bordered table-striped">
                                    <tbody>
                                        <tr>
                                            <th style={{ width: "30%" }}>Name Given</th>
                                            <td>{booking?.name || "N/A"}</td>
                                        </tr>
                                        <tr>
                                            <th>Gender</th>
                                            <td style={{ textTransform: "capitalize" }}>{booking?.gender || "N/A"}</td>
                                        </tr>
                                        <tr>
                                            <th>Date of Birth</th>
                                            <td>{booking?.birthDate ? new Date(booking.birthDate).toLocaleDateString() : "N/A"}</td>
                                        </tr>
                                        <tr>
                                            <th>Time of Birth</th>
                                            <td>{booking?.birthTime || "N/A"}</td>
                                        </tr>
                                        <tr>
                                            <th>Place of Birth</th>
                                            <td>{booking?.place || "N/A"}</td>
                                        </tr>
                                        <tr>
                                            <th>Situation Context</th>
                                            <td>{booking?.situation || "N/A"}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div className="col-md-6">
                                <h6 className="main-content-label mb-3">Question & Payment</h6>
                                <table className="table table-bordered table-striped">
                                    <tbody>
                                        {/* <tr>
                                            <th style={{ width: "30%" }}>Base Question</th>
                                            <td>{booking?.report_question?.question || "N/A"}</td>
                                        </tr> */}
                                        <tr>
                                            <th>User Question Text</th>
                                            <td>
                                                {(() => {
                                                    let qData = booking?.question;
                                                    if (!qData) return "N/A";
                                                    let qArray = [];
                                                    if (typeof qData === 'string') {
                                                        try {
                                                            qArray = JSON.parse(qData);
                                                            if (!Array.isArray(qArray)) qArray = [qArray];
                                                        } catch (e) {
                                                            let cleanStr = qData.trim();
                                                            if (cleanStr.startsWith('[') && cleanStr.endsWith(']')) {
                                                                cleanStr = cleanStr.substring(1, cleanStr.length - 1);
                                                                qArray = cleanStr.split(',').map(s => s.trim()).filter(s => s);
                                                            } else {
                                                                qArray = [qData];
                                                            }
                                                        }
                                                    } else if (Array.isArray(qData)) {
                                                        qArray = qData;
                                                    } else {
                                                        qArray = [qData];
                                                    }

                                                    if (qArray.length > 0) {
                                                        return (
                                                            <div style={{ whiteSpace: "pre-wrap" }}>
                                                                {qArray.map((q, idx) => (
                                                                    <div key={idx} className="mb-1">
                                                                        {qArray.length > 1 ? `${idx + 1}. ` : "• "}{q}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        );
                                                    }
                                                    return "N/A";
                                                })()}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>Payment ID</th>
                                            <td>{booking?.Payment?.transaction_id || "N/A"}</td>
                                        </tr>
                                        <tr>
                                            <th>Payment Method</th>
                                            <td>{booking?.Payment?.payment_method || "N/A"}</td>
                                        </tr>
                                        <tr>
                                            <th>Payment Status</th>
                                            <td>
                                                <span className={`badge ${booking?.Payment?.payment_status === 'completed' ? 'bg-success' : 'bg-warning'}`}>
                                                    {booking?.Payment?.payment_status || "Pending"}
                                                </span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ViewQuestionBooking;
